import { useMemo, useState, useSyncExternalStore } from 'react';
import type { KartiMood } from '../components/brand/Karti';

/* ------------------------------------------------------------------ */
/* Cheekiness level (stored in localStorage, shared across components) */
/* ------------------------------------------------------------------ */

export type KartiLevel = 'sanft' | 'normal' | 'frech';
export const KARTI_LEVELS: { id: KartiLevel; label: string }[] = [
  { id: 'sanft', label: 'Sanft' },
  { id: 'normal', label: 'Normal' },
  { id: 'frech', label: 'Frech' },
];
const LEVEL_KEY = 'karti.level';
const LEVEL_MAX: Record<KartiLevel, number> = { sanft: 0, normal: 1, frech: 2 };
const listeners = new Set<() => void>();

function readLevel(): KartiLevel {
  try {
    const v = localStorage.getItem(LEVEL_KEY);
    if (v === 'sanft' || v === 'normal' || v === 'frech') return v;
  } catch {
    /* storage unavailable */
  }
  return 'normal';
}

export function setKartiLevel(level: KartiLevel) {
  try {
    localStorage.setItem(LEVEL_KEY, level);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

export function useKartiLevel(): [KartiLevel, (l: KartiLevel) => void] {
  const level = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    readLevel,
    () => 'normal' as KartiLevel
  );
  return [level, setKartiLevel];
}

/* ------------------------------------------------------------------ */
/* Line pools                                                          */
/* ------------------------------------------------------------------ */

/** level: 0 = warm (all), 1 = mildly cheeky (normal, frech), 2 = cheeky (frech only). */
export interface KartiLine {
  text: string;
  level: 0 | 1 | 2;
  mood: KartiMood;
}
const L = (text: string, level: 0 | 1 | 2 = 0, mood: KartiMood = 'neutral'): KartiLine => ({ text, level, mood });

const LEARNED: KartiLine[] = [
  L('Heute schon gelernt. Das reicht, morgen geht es weiter.', 0, 'happy'),
  L('Sauber. Heute hast du geliefert.', 0, 'happy'),
  L('Streak gesichert. Ich bin zufrieden.', 0, 'happy'),
  L('Das war eine gute Runde. Gönn dir eine Pause.', 0, 'happy'),
  L('Jeden Tag ein Stück. So funktioniert es.', 0, 'happy'),
  L('Heute schon erledigt. Das nenne ich Disziplin.', 1, 'happy'),
  L('Ich habe nichts zu meckern. Ungewohnt.', 1, 'happy'),
  L('Du warst schneller als ich dachte.', 1, 'wow'),
  L('Streber. Ich meine das als Kompliment.', 2, 'happy'),
  L('Ich hätte fast nichts mehr zu motzen. Fast.', 2, 'happy'),
  L('Wenn das so weitergeht, werde ich arbeitslos.', 2, 'happy'),
];

const DUE: KartiLine[] = [
  L('{n} Karten warten. Fünf Minuten reichen.'),
  L('Kleine Portionen schlagen Marathon. Fang mit ein paar an.'),
  L('Zehn Karten, dann ist der Kopf warm.'),
  L('Ein bisschen jeden Tag bringt mehr als viel am Sonntag.'),
  L('{n} Karten sind bereit. Du bist es auch.', 0, 'happy'),
  L('Ich habe nachgezählt. Es sind immer noch Karten da.', 1, 'nag'),
  L('Die Karten laufen nicht weg. Aber sie werden mehr.', 1, 'nag'),
  L('Dein Streak? Der zittert schon.', 1, 'nag'),
  L('Nur eine Runde. Dann lasse ich dich in Ruhe. Vielleicht.', 1, 'nag'),
  L('{n} Karten. Ich zähle mit.', 2, 'nag'),
  L('Die Karten starren schon. Ich auch.', 2, 'nag'),
  L('Den Tab hast du ja eh offen. Also los.', 2, 'nag'),
];

const RELAXED: KartiLine[] = [
  L('Alles ruhig. Ich bin da, wenn du etwas lernen willst.', 0, 'sleep'),
  L('Nichts fällig. Perfekt für neue Wörter.'),
  L('Heute entspannt. Morgen kommen neue Karten.', 0, 'sleep'),
  L('Wer regelmäßig lernt, hat auch mal frei.', 0, 'happy'),
  L('Leer. Ich weiß nicht, was ich mit mir anfangen soll.', 1, 'sleep'),
  L('Nichts zu tun? Dann füll doch einen Kurs auf.', 1),
  L('Ich habe nachgezählt. Wirklich nichts da. Beeindruckend.', 1),
  L('Keine Karten fällig. Ich langweile mich trotzdem.', 2, 'sleep'),
  L('Ruhe vor dem Sturm. Ich habe da so ein Gefühl.', 2),
];

const STREAK_BIG: KartiLine[] = [
  L('{s} Tage am Stück. Das merkt man.', 0, 'wow'),
  L('{s} Tage in Folge. Ich bin beeindruckt.', 0, 'wow'),
];

export type KartiScreen =
  | 'dashboard'
  | 'courses'
  | 'courseDetail'
  | 'statistics'
  | 'profile'
  | 'login'
  | 'register'
  | 'sidebar'
  | 'loading'
  | 'notFound'
  | 'empty'
  | 'tip';

const SCREEN: Partial<Record<KartiScreen, KartiLine[]>> = {
  courses: [
    L('Such dir einen Kurs aus. Ich schaue zu.'),
    L('Welcher Kurs darf heute dran glauben?', 1),
    L('Ich habe bei der Kursauswahl keine Meinung. Okay, doch.', 2),
  ],
  courseDetail: [
    L('Schau dir die Phasen an. Jedes Wort wandert nach oben.'),
    L('Wähl einen Modus. Der Rest ergibt sich.'),
    L('Das Diagramm sieht gut aus, wenn du lernst. Nur so als Info.', 1),
    L('Hübsches Diagramm. Noch hübscher mit mehr Phase 7.', 2),
  ],
  statistics: [
    L('Zahlen lügen nicht. Dranbleiben zahlt sich aus.'),
    L('Jeder Balken war mal ein kleiner Anfang.'),
    L('Die Statistik merkt sich alles. Ich auch.', 1),
    L('Schau ruhig. Aber lernen bringt mehr als Zugucken.', 2),
  ],
  profile: [
    L('Hier stellst du ein, wie sehr ich nerven darf.', 1),
    L('Deine Einstellungen. Meine Meinung dazu: lernen.', 2),
    L('Alles nach deinem Geschmack einstellen. Auch mich.'),
    L('Dein Konto, deine Regeln. Meine auch ein bisschen.'),
  ],
  login: [
    L('Willkommen zurück. Die Karten haben dich vermisst.', 0, 'happy'),
    L('Kurz anmelden, dann geht es los.'),
    L('Passwort vergessen? Ich nicht. Aber ich verrate nichts.', 2),
  ],
  register: [L('Schön, dass du dabei bist. Fang klein an.', 0, 'happy'), L('Kurz ausfüllen, dann lernen wir los.')],
  sidebar: [
    L('Ein Kurs am Tag hält den Streak am Leben.'),
    L('Kleine Runde zwischendurch? Ich bin dabei.'),
    L('Ich bleibe hier. Falls du mich brauchst.', 1),
    L('Bildschirm an, Kopf an. Fast.', 2),
  ],
  loading: [
    L('Moment. Ich mische die Karten.'),
    L('Kleinen Augenblick. Ich sortiere noch.', 0, 'sleep'),
    L('Gleich da. Ich bin nur langsam wach.', 1, 'sleep'),
  ],
  notFound: [L('Hier ist nichts. Nicht einmal eine Karte.', 0, 'sad'), L('Das habe ich nicht gefunden. Ich habe wirklich gesucht.', 0, 'sad')],
  empty: [
    L('Hier ist noch alles leer. Das ändern wir.', 0, 'sleep'),
    L('Noch nichts da. Fang einfach an.', 0, 'sleep'),
    L('So viel Platz. Ich weiß gar nicht, wohin mit mir.', 1, 'sleep'),
  ],
  tip: [
    L('Kurze Runden jeden Tag schlagen lange Sessions selten.'),
    L('Sprich das Wort laut aus. Das bleibt besser hängen.'),
    L('Wiederhole falsche Karten direkt noch einmal.'),
    L('Lerne lieber morgens und abends, als nur einmal lange.'),
    L('Denk dir einen Satz zum Wort aus. Das hilft.'),
  ],
};

/** Reactions during a quiz. */
export const QUIZ_LINES = {
  correct: [L('Sitzt.', 0, 'happy'), L('Gut.', 0, 'happy'), L('Genau so.', 0, 'happy'), L('Nicht schlecht.', 1, 'happy'), L('Das war leicht, oder?', 2, 'happy')],
  streak3: [L('Läuft.', 0, 'happy'), L('Es läuft.', 0, 'happy'), L('Drei am Stück.', 0, 'happy')],
  streak5: [L('Läuft. Weiter so.', 0, 'wow'), L('Fünf am Stück. Nicht schlecht.', 0, 'wow'), L('Du bist heiß gelaufen.', 1, 'wow')],
  wrong: [L('Autsch. Passiert.', 0, 'sad'), L('Nicht schlimm. Das kommt wieder.', 0, 'sad'), L('Nächstes Mal sitzt es.', 0, 'sad'), L('Autsch. Das tat sogar mir weh.', 2, 'sad')],
  half: [L('Halbzeit. Du bist dabei.', 0, 'neutral'), L('Die Hälfte ist geschafft.', 0, 'happy')],
};

/** Result screen by accuracy (0-100). */
export const RESULT_LINES = {
  wow: [L('Fast alles richtig. Ich bin sprachlos.', 0, 'wow'), L('Das war stark. Wirklich.', 0, 'wow'), L('Fehlerfrei fast. Gib mir fünf.', 1, 'wow')],
  happy: [L('Gute Runde. Das sitzt schon.', 0, 'happy'), L('Das war solide. Weiter so.', 0, 'happy'), L('Sauber gemacht. Morgen mehr.', 1, 'happy')],
  neutral: [L('Geht noch besser. Üb die Fehler gleich nochmal.', 0, 'neutral'), L('Mittelfeld. Noch eine Runde, dann sitzt es.', 1, 'neutral')],
  sad: [L('Das war zäh. Kopf hoch, die nächste Runde wird besser.', 0, 'sad'), L('Nicht dein Tag. Aber du bist drangeblieben.', 0, 'sad'), L('Autsch. Kleine Runde nochmal, dann sieht es anders aus.', 1, 'sad')],
};

export function resultFor(pct: number): { mood: KartiMood; pool: KartiLine[]; tip: boolean } {
  if (pct >= 90) return { mood: 'wow', pool: RESULT_LINES.wow, tip: false };
  if (pct >= 70) return { mood: 'happy', pool: RESULT_LINES.happy, tip: false };
  if (pct >= 40) return { mood: 'neutral', pool: RESULT_LINES.neutral, tip: true };
  return { mood: 'sad', pool: RESULT_LINES.sad, tip: false };
}

/* ------------------------------------------------------------------ */
/* Picking                                                             */
/* ------------------------------------------------------------------ */

export function pickLine(pool: KartiLine[], level: KartiLevel, seed: number): KartiLine {
  const allowed = pool.filter((l) => l.level <= LEVEL_MAX[level]);
  const list = allowed.length ? allowed : pool;
  return list[Math.abs(Math.floor(seed)) % list.length];
}

function fill(text: string, ctx: KartiContext) {
  return text.replace('{n}', String(ctx.due ?? 0)).replace('{s}', String(ctx.streak ?? 0));
}

export interface KartiContext {
  due?: number;
  streak?: number;
  learnedToday?: boolean;
  /** true once activity data is known; before that generic lines are used */
  ready?: boolean;
}

export interface KartiMessage {
  text: string;
  mood: KartiMood;
}

const VISIT_KEY = 'karti.visits';

/** Bumps and returns the visit counter of a screen (rotates idle lines from visit to visit). */
function nextVisit(screen: string): number {
  try {
    const all = JSON.parse(localStorage.getItem(VISIT_KEY) || '{}') as Record<string, number>;
    const n = (all[screen] ?? 0) + 1;
    all[screen] = n;
    localStorage.setItem(VISIT_KEY, JSON.stringify(all));
    return n;
  } catch {
    return Math.floor(Date.now() / 60000);
  }
}

function dayNumber() {
  return Math.floor((Date.now() - new Date().getTimezoneOffset() * 60000) / 86400000);
}

function poolFor(screen: KartiScreen, ctx: KartiContext): KartiLine[] {
  const situational = () => {
    if (ctx.ready === false) return SCREEN.courses!;
    if (ctx.learnedToday && !(ctx.due && ctx.due > 0)) {
      return (ctx.streak ?? 0) >= 7 ? [...LEARNED, ...STREAK_BIG] : LEARNED;
    }
    if ((ctx.due ?? 0) > 0) return DUE;
    return RELAXED;
  };
  switch (screen) {
    case 'dashboard':
    case 'courses':
      return situational();
    case 'statistics':
      return ctx.ready !== false && !ctx.learnedToday && (ctx.due ?? 0) > 0 ? [...SCREEN.statistics!, ...DUE.slice(0, 5)] : SCREEN.statistics!;
    default:
      return SCREEN[screen] ?? RELAXED;
  }
}

/**
 * What Karti says on a screen. The line is stable during one visit (mount) and rotates
 * by day and visit counter. Reads the cheekiness level from localStorage ("normal" default).
 */
export function useKartiMessage(screen: KartiScreen, ctx: KartiContext = {}): KartiMessage {
  const [level] = useKartiLevel();
  const [seed] = useState(() => dayNumber() + nextVisit(screen) * 3);
  const { due, streak, learnedToday, ready } = ctx;
  return useMemo(() => {
    const line = pickLine(poolFor(screen, { due, streak, learnedToday, ready }), level, seed);
    return { text: fill(line.text, { due, streak }), mood: line.mood };
  }, [screen, level, seed, due, streak, learnedToday, ready]);
}

/** Convenience for pages: context from activity data (undefined while loading). */
export function activityContext(
  activity: { due: { total: number }; streak: number; learnedToday: boolean } | null | undefined
): KartiContext {
  return activity
    ? { due: activity.due.total, streak: activity.streak, learnedToday: activity.learnedToday, ready: true }
    : { ready: false };
}

/** Study tips for the result screen. */
export const QUIZ_TIPS: KartiLine[] = SCREEN.tip!;
